import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DayPicker, Matcher } from 'react-day-picker'
import { enUS, ro } from 'react-day-picker/locale'
import Tippy from '@tippyjs/react'
import { format } from 'date-fns'

import Icon from '../../ui/Icon'
import { displayDate } from '../../../utils/dateUtils'

import 'react-day-picker/dist/style.css'

interface InputDateProps {
  id?: string
  name: string
  value: Date | null
  required?: boolean
  label?: string
  className?: string
  disabled?: boolean
  disablePastDates?: boolean
  disableFutureDates?: boolean
  tooltip?: string
  infoMessage?: string
  errorMessage?: string
  invalid?: boolean
  minDate?: Date
  maxDate?: Date
  onChange: (name: string, value: Date | null, isValid: boolean) => void
  onValidate?: (value: string) => boolean
}

export const InputDate = ({
  id,
  name,
  value,
  required = false,
  label,
  className = '',
  disabled = false,
  disablePastDates = true,
  disableFutureDates = false,
  tooltip,
  infoMessage,
  errorMessage,
  invalid = false,
  minDate,
  maxDate,
  onChange,
  onValidate,
}: InputDateProps) => {
  const { t, i18n } = useTranslation('inputs')

  const [isError, setIsError] = useState(false)
  const [isTouch, setIsTouch] = useState(false)
  const [isFocus, setIsFocus] = useState(false)
  const [open, setOpen] = useState(false)

  const wrapperRef = useRef<HTMLDivElement>(null)
  const generatedId = useId()
  const inputId = id ?? `${generatedId}-input`
  const pickerId = `${inputId}-picker`

  const isRequired = isTouch && required && !value

  const fieldClass = [
    'field',
    'input-date',
    className,
    required && 'required',
    invalid && 'is-error',
    !isFocus && isRequired && 'is-required',
    !isFocus && isError && 'is-error',
  ]
    .filter(Boolean)
    .join(' ')

  const language = i18n.resolvedLanguage ?? i18n.language

  const locale = language.startsWith('ro') ? ro : enUS

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const disabledDays: Matcher[] = []

  if (disablePastDates) {
    disabledDays.push({ before: today })
  }

  if (disableFutureDates) {
    disabledDays.push({ after: today })
  }

  if (minDate) {
    disabledDays.push({ before: minDate })
  }

  if (maxDate) {
    disabledDays.push({ after: maxDate })
  }

  const calendarEndMonth = maxDate
    ? maxDate
    : disableFutureDates
      ? today
      : new Date(today.getFullYear() + 100, 11, 31)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setOpen(false)
        setIsFocus(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleOpen = () => {
    if (disabled) {
      return
    }

    setIsTouch(true)
    setIsFocus(true)
    setOpen(previous => !previous)
  }

  const handleSelect = useCallback(
    (day: Date | undefined) => {
      if (!day) {
        return
      }

      const customValid = onValidate ? onValidate(displayDate(day)) : true

      setIsTouch(true)
      setIsError(!customValid)
      setOpen(false)
      setIsFocus(false)

      onChange(name, day, customValid)
    },
    [name, onChange, onValidate]
  )

  const handleClear = useCallback(() => {
    setIsTouch(true)
    setIsError(false)
    setOpen(false)
    setIsFocus(false)

    onChange(name, null, true)
  }, [name, onChange])

  const labelElement = <label htmlFor={inputId}>{label}</label>

  return (
    <div ref={wrapperRef} className={fieldClass}>
      {label &&
        (tooltip ? (
          <Tippy content={tooltip} placement="top" className="ph-tooltip">
            {labelElement}
          </Tippy>
        ) : (
          labelElement
        ))}

      <div
        className={[
          'input-icon-wrap',
          infoMessage && value && !required && 'has-multiple-date-actions',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <input
          id={inputId}
          className="input"
          type="text"
          name={name}
          value={value ? format(value, 'dd-MM-yyyy') : ''}
          placeholder={disabled ? '' : t('select-date')}
          required={required}
          disabled={disabled}
          readOnly
          role="combobox"
          aria-expanded={open}
          aria-haspopup="dialog"
          aria-controls={pickerId}
          aria-invalid={invalid || isError || isRequired}
          onClick={handleOpen}
          onFocus={() => setIsFocus(true)}
        />

        {(infoMessage || (value && !required)) && (
          <div className="input-date-actions">
            {value && !required && (
              <button
                type="button"
                className="input-date-action"
                aria-label={t('clear-date')}
                title={t('clear-date')}
                onClick={handleClear}
              >
                <Icon name="x" size={16} />
              </button>
            )}
            {infoMessage && (
              <Tippy
                content={infoMessage}
                placement="top"
                className="ph-tooltip"
              >
                <div className="input-date-action">
                  <Icon name="info" size={16} />
                </div>
              </Tippy>
            )}
          </div>
        )}

        {open && !disabled && (
          <div
            id={pickerId}
            className="input-date-picker"
            role="dialog"
            aria-label={label ?? t('select-date')}
          >
            <DayPicker
              mode="single"
              locale={locale}
              selected={value ?? undefined}
              defaultMonth={value ?? minDate ?? today}
              endMonth={calendarEndMonth}
              onSelect={handleSelect}
              disabled={disabledDays.length > 0 ? disabledDays : undefined}
              showOutsideDays
              captionLayout="dropdown"
              navLayout="around"
            />
          </div>
        )}
      </div>

      {errorMessage && !isFocus && isError && (
        <div className="field-msg err" role="alert">
          <span className="ico">
            <Icon name="warn" size={13} />
          </span>

          {errorMessage}
        </div>
      )}
    </div>
  )
}
